import { CHARACTER_ACTIONS, type CharacterActionKey } from "@/character/actions";
import type { GridTuning } from "@/character/draft";
import {
  GRID_COLUMNS,
  GRID_ROWS,
  hasAnyRowSplit,
  type CharacterGridSpec,
  frameIndex,
  resolveSheetLayout,
  sourceFrameRect,
  spriteRegionHeight,
  spriteRegionWidth,
} from "@/character/grid";
import type { CharacterProfile } from "@/character/storage";

export interface ParsedSpritesheet {
  spritesheetDataUrl: string;
  grid: CharacterGridSpec;
  actions: Record<CharacterActionKey, string>;
}

export async function parseCharacterSpritesheet(
  dataUrl: string,
  tuning: GridTuning,
): Promise<ParsedSpritesheet> {
  const image = await loadImage(dataUrl);
  const layout = resolveSheetLayout(image, tuning);
  const { grid } = layout;
  const cropX = grid.regionX + grid.alignX;
  const cropY = grid.regionY + grid.alignY;

  const cropCanvas = document.createElement("canvas");
  const cropW = spriteRegionWidth(grid);
  const cropH = spriteRegionHeight(grid);
  cropCanvas.width = cropW;
  cropCanvas.height = cropH;
  const cropCtx = cropCanvas.getContext("2d");
  if (!cropCtx) {
    throw new Error("แยกเฟรมจากสไปรต์ชีตไม่สำเร็จ");
  }
  if (hasAnyRowSplit(tuning)) {
    cropCtx.fillStyle = "#0a0e14";
    cropCtx.fillRect(0, 0, cropW, cropH);
    for (let row = 0; row < GRID_ROWS; row += 1) {
      for (let col = 0; col < GRID_COLUMNS; col += 1) {
        const rect = sourceFrameRect(layout, col, row, tuning);
        const dx = col * grid.frameWidth;
        const dy = row * grid.frameHeight;
        cropCtx.drawImage(
          image,
          rect.sx,
          rect.sy,
          rect.sw,
          rect.sh,
          dx,
          dy,
          grid.frameWidth,
          grid.frameHeight,
        );
      }
    }
  } else {
    cropCtx.drawImage(image, cropX, cropY, cropW, cropH, 0, 0, cropW, cropH);
  }
  const spritesheetDataUrl = cropCanvas.toDataURL("image/png");

  const croppedImage = await loadImage(spritesheetDataUrl);
  const normalizedGrid: CharacterGridSpec = {
    columns: GRID_COLUMNS,
    rows: GRID_ROWS,
    frameWidth: grid.frameWidth,
    frameHeight: grid.frameHeight,
    regionX: 0,
    regionY: 0,
    sheetColumns: GRID_COLUMNS,
    alignX: 0,
    alignY: 0,
  };
  const croppedLayout = {
    grid: normalizedGrid,
    sheetWidth: cropW,
    sheetHeight: cropH,
  };

  const canvas = document.createElement("canvas");
  const ctx = canvas.getContext("2d");
  if (!ctx) {
    throw new Error("แยกเฟรมจากสไปรต์ชีตไม่สำเร็จ");
  }

  const actions = {} as Record<CharacterActionKey, string>;

  for (const action of CHARACTER_ACTIONS) {
    const rect = sourceFrameRect(croppedLayout, 0, action.row);
    canvas.width = Math.round(rect.sw);
    canvas.height = Math.round(rect.sh);
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    ctx.drawImage(
      croppedImage,
      rect.sx,
      rect.sy,
      rect.sw,
      rect.sh,
      0,
      0,
      canvas.width,
      canvas.height,
    );
    actions[action.key] = canvas.toDataURL("image/png");
  }

  return {
    spritesheetDataUrl,
    grid: normalizedGrid,
    actions,
  };
}

export function idleAnimationFrameRange(
  grid: CharacterGridSpec,
): { start: number; end: number } {
  return actionAnimationFrameRange(grid, "idle");
}

export function runAnimationFrameRange(
  grid: CharacterGridSpec,
): { start: number; end: number } {
  return actionAnimationFrameRange(grid, "run");
}

export function sitAnimationFrameRange(
  grid: CharacterGridSpec,
): { start: number; end: number } {
  return actionAnimationFrameRange(grid, "sit");
}

export function attackAnimationFrameRange(
  grid: CharacterGridSpec,
): { start: number; end: number } {
  return actionAnimationFrameRange(grid, "attack");
}

function actionAnimationFrameRange(
  grid: CharacterGridSpec,
  key: CharacterActionKey,
): { start: number; end: number } {
  const row = CHARACTER_ACTIONS.find((a) => a.key === key)?.row ?? 0;
  const start = frameIndex(row, 0, grid.columns);
  const end = start + grid.columns - 1;
  return { start, end };
}

export function profileToGameArt(profile: CharacterProfile): {
  playerSpritesheetUrl: string;
  playerSpritesheetGrid: CharacterGridSpec;
  playerIdleTextureUrl: string;
} {
  return {
    playerSpritesheetUrl: profile.spritesheetDataUrl,
    playerSpritesheetGrid: profile.grid,
    playerIdleTextureUrl: profile.actions.idle,
  };
}

function loadImage(dataUrl: string): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => resolve(img);
    img.onerror = () => reject(new Error("โหลดสไปรต์ชีตไม่สำเร็จ"));
    img.src = dataUrl;
  });
}
